import { useEffect, useState } from 'react';

import './StoreBadges.css';

/**
 * The two stores' own badges (2026-10-26 Riley: "the way the big sites do it") —
 * Apple's "Download on the App Store" (developer.apple.com, black, US-UK) and
 * Google's "Get it on Google Play" (play.google.com/intl/en_us/badges, its
 * transparent clear space trimmed so both stand at one height; the layout gives
 * the clear space back). Each store requires its own artwork for these words,
 * so neither is redrawn here.
 *
 * Where they stand: the footer and the homepage's "Get Ember on your phone" show
 * both, on every device (the footer is also on the static authored pages, which
 * know nothing about the phone); the horn's bubble on the homepage shows the
 * phone's own (Announcements.tsx).
 *
 * The Play link carries an Install Referrer with utm_source=ember_web, so an
 * install that started on the web is counted as Web → App (ad-spend.js) and,
 * once the page's script has run, a one-time handoff token joins the install
 * back to the browser that tapped (app-installs.js) — every Play badge, the
 * footer's and the horn's too (2026-10-31 jam: from 10-26 only the nav's
 * carried one, and the others' installs read as Unknown). A badge on a page
 * no script ran on carries none; its install is paired by time to the Play
 * badge tap Play itself recorded. The App Store has no install referrer: an
 * iPhone install is paired by time to the latest App Store badge tap in the
 * half hour before its first open (2026-10-27).
 */

export const APP_STORE_URL = 'https://apps.apple.com/app/id6802176724';
const PLAY_LISTING = 'https://play.google.com/store/apps/details?id=com.oriscen.ember';

export type Store = 'ios' | 'android';

/** The Play link: utm_medium names where the badge stood; `handoffId` empty = no token (still a valid link). */
export function playStoreUrl(medium: string, handoffId = ''): string {
  const referrer = `utm_source=ember_web&utm_medium=${encodeURIComponent(medium)}&utm_campaign=store_badge${handoffId ? `&ember_handoff=${handoffId}` : ''}`;
  return `${PLAY_LISTING}&referrer=${encodeURIComponent(referrer)}`;
}

/** 16 random hex characters; '' where the browser offers no crypto (the link then carries none). */
export function newHandoffId(): string {
  try {
    const bytes = new Uint8Array(8);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}

/** One badge. `discovery` is where it stands (the analytics name's placement and the Play referrer's medium). */
export function StoreBadge({ store, discovery, handoffId = '', size = 'md' }: {
  store: Store;
  discovery: string;
  handoffId?: string;
  size?: 'sm' | 'md';
}) {
  const ios = store === 'ios';
  return (
    <a
      className={`store-badge store-badge-${store} store-badge-${size}`}
      href={ios ? APP_STORE_URL : playStoreUrl(discovery, handoffId)}
      target="_blank"
      rel="noopener"
      data-log="app_store_badge"
      data-log-state={store}
      data-log-discovery={discovery}
      data-log-handoff-id={handoffId || undefined}
    >
      <img
        src={ios ? '/badges/app-store.svg' : '/badges/google-play.png'}
        alt={ios ? 'Download on the App Store' : 'Get it on Google Play'}
        width={ios ? 120 : 134}
        height={40}
        decoding="async"
      />
    </a>
  );
}

/** Both badges side by side. The Play badge's token is minted in the browser, after the server's markup has hydrated. */
export default function StoreBadges({ discovery, className = '' }: { discovery: string; className?: string }) {
  const [handoffId, setHandoffId] = useState('');
  useEffect(() => { setHandoffId(newHandoffId()); }, []);
  return (
    <div className={`store-badges${className ? ` ${className}` : ''}`} data-log-seen="app_store_badge" data-log-discovery={discovery}>
      <StoreBadge store="ios" discovery={discovery} />
      <StoreBadge store="android" discovery={discovery} handoffId={handoffId} />
    </div>
  );
}
